<script setup lang="ts">
/**
 * BreadcrumbsComponent — Donor Portal
 *
 * Renders a horizontal breadcrumb trail derived from the current route's
 * `meta.breadcrumbs` (or auto-generated from the path when absent).
 *
 * - The first crumb always shows a Home icon alongside its label,
 *   whether it is a link (e.g. "Home → Profile") or the current page (e.g. "Dashboard").
 * - Each crumb with a `to` value is rendered as a RouterLink.
 * - The last crumb (no `to`) is rendered as plain text with `aria-current="page"`.
 * - Items are separated by a ChevronRight icon.
 *
 * @example
 * <!-- place inside AppHeader or AppLayout -->
 * <BreadcrumbsComponent />
 */
import { ChevronRight } from 'lucide-vue-next';
import { Icon } from '@tms-phoenix/ui-vue';
import { useBreadcrumbs } from '@/shared/composables/useBreadcrumbs';
import { useLocale } from '@/shared/composables/useLocale';

const { breadcrumbs } = useBreadcrumbs();
const { t } = useLocale();
</script>

<template>
  <nav
    class="flex"
    :aria-label="t('nav.breadcrumbAriaLabel')"
  >
    <ol class="flex items-center space-x-1">
      <li
        v-for="(item, index) in breadcrumbs"
        :key="index"
        class="flex items-center"
      >
        <!-- Separator (hidden before the first item) -->
        <ChevronRight
          v-if="index > 0"
          class="mr-2 h-4 w-4 shrink-0 text-gray-400"
          aria-hidden="true"
        />

        <!-- Linked crumb -->
        <RouterLink
          v-if="item.to"
          :to="item.to"
          class="flex items-center text-sm font-medium text-primary hover:text-gray-700"
        >
          <Icon
            v-if="index === 0"
            name="Home"
            size-class="h-4 w-4 shrink-0"
            color="text-primary-600"
            class="mr-1.5"
            :decorative="true"
          />
          <component
            :is="item.icon"
            v-else-if="item.icon"
            class="mr-1.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          {{ item.label }}
        </RouterLink>

        <!-- Current page (non-linked) -->
        <span
          v-else
          class="flex items-center text-sm font-medium text-gray-500"
          aria-current="page"
        >
          <Icon
            v-if="index === 0"
            name="Home"
            size-class="h-4 w-4 shrink-0"
            color="text-primary-600"
            class="mr-1.5"
            :decorative="true"
          />
          <component
            :is="item.icon"
            v-else-if="item.icon"
            class="mr-1.5 h-4 w-4 shrink-0"
            aria-hidden="true"
          />
          {{ item.label }}
        </span>
      </li>
    </ol>
  </nav>
</template>
